import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Image, Video, Send, Loader } from 'lucide-react';
import { apiCall } from '../utils/api';
import './CreatePost.css';

const getUserIdFromJwt = () => {
    try {
        const token = document.cookie
            .split('; ')
            .find((row) => row.startsWith('access_token='))
            ?.split('=')[1];
        if (!token) return null;
        const payloadBase64 = token.split('.')[1];
        const payload = JSON.parse(atob(payloadBase64));
        return payload.user_id || payload.sub || payload.id || null;
    } catch (e) {
        console.error('Error parsing JWT for feed post:', e);
        return null;
    }
};

const resolveOnboardingPayload = (data) => {
    if (!data) return null;
    return (
        data.onboarding_details?.onboarding_choices?.onboarding_choices || // in case of double nesting
        data.onboarding_details?.onboarding_choices ||
        data.onboarding_details?.onboarding ||
        data.onboarding_choices ||
        data.onboarding ||
        data
    );
};

const extractOnboardingName = (payload) => {
    if (!payload) return null;
    const resolved = resolveOnboardingPayload(payload) || payload;
    const candidateNames = [
        resolved?.name,
        resolved?.first_name,
        resolved?.display_name,
        payload?.name,
        payload?.first_name,
        payload?.display_name,
        payload?.onboarding_details?.name,
        payload?.onboarding_details?.first_name,
        payload?.onboarding_details?.display_name,
    ];

    for (const candidate of candidateNames) {
        if (typeof candidate === 'string' && candidate.trim()) {
            return candidate.trim();
        }
    }

    return null;
};

const getProfileDisplayNameFromMerged = (mergedData) => {
    const name = mergedData?.name;
    if (name && name.trim()) {
        return name;
    }
    const email = mergedData?.email;
    if (email && typeof email === 'string') {
        return email.split('@')[0];
    }
    return 'User';
};

const CreatePost = ({ isOpen, onClose, onPostCreated }) => {
    const [text, setText] = useState('');
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaPreview, setMediaPreview] = useState(null);
    const [mediaPreviewUrl, setMediaPreviewUrl] = useState(null);
    const [mediaType, setMediaType] = useState(null); // 'image' or 'video'
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(null);
    const [profileDisplayName, setProfileDisplayName] = useState('');
    
    const fileInputRef = useRef(null);

    const resetForm = useCallback(() => {
        setText('');
        if (mediaPreviewUrl) {
            URL.revokeObjectURL(mediaPreviewUrl);
        }
        setMediaFile(null);
        setMediaPreview(null);
        setMediaPreviewUrl(null);
        setMediaType(null);
        setIsUploading(false);
        setUploadProgress(0);
        setError(null);
    }, [mediaPreviewUrl]);

    useEffect(() => {
        return () => {
            if (mediaPreviewUrl) {
                URL.revokeObjectURL(mediaPreviewUrl);
            }
        };
    }, [mediaPreviewUrl]);

    useEffect(() => {
        if (!isOpen) {
            setProfileDisplayName('');
            return;
        }

        let cancelled = false;

        const fetchProfileName = async () => {
            try {
                const userId = getUserIdFromJwt();
                console.log('========== CREATE POST NAME DEBUG ==========');
                console.log('🔑 User ID from JWT:', userId);
                
                // Fetch BOTH user details and onboarding - same as profile page
                const [userDetailsResponse, onboardingResponse] = await Promise.all([
                    apiCall.get('/users/get/user/details'),
                    userId ? apiCall.get(`/users/onboarding/${userId}`) : Promise.resolve(null),
                ]);

                console.log('👤 RAW userDetailsResponse:');
                console.log(userDetailsResponse);
                console.log('👤 userDetailsResponse.name:', userDetailsResponse?.name);
                console.log('👤 userDetailsResponse.data?.name:', userDetailsResponse?.data?.name);
                
                console.log('📋 RAW onboardingResponse:');
                console.log(onboardingResponse);

                const userDetails = userDetailsResponse?.data || userDetailsResponse || {};
                console.log('👤 userDetails after extraction:', userDetails);
                console.log('👤 userDetails.name:', userDetails?.name);
                
                // Try ALL possible paths to find onboarding name
                console.log('--- Checking all onboarding paths ---');
                console.log('Path 1 - onboardingResponse?.onboarding_details?.onboarding_choices:', onboardingResponse?.onboarding_details?.onboarding_choices);
                console.log('Path 2 - onboardingResponse?.onboarding_choices:', onboardingResponse?.onboarding_choices);
                console.log('Path 3 - onboardingResponse?.onboarding:', onboardingResponse?.onboarding);
                console.log('Path 4 - onboardingResponse?.data?.onboarding_details?.onboarding_choices:', onboardingResponse?.data?.onboarding_details?.onboarding_choices);
                console.log('Path 5 - onboardingResponse?.data?.onboarding_choices:', onboardingResponse?.data?.onboarding_choices);
                console.log('Path 6 - onboardingResponse?.data:', onboardingResponse?.data);
                
                // Extract onboarding_choices from nested structure
                const resolvedOnboardingData =
                    onboardingResponse?.data?.onboarding_details?.onboarding_choices ||
                    onboardingResponse?.onboarding_details?.onboarding_choices ||
                    onboardingResponse?.data?.onboarding_choices ||
                    onboardingResponse?.onboarding_choices ||
                    onboardingResponse?.data?.onboarding ||
                    onboardingResponse?.onboarding ||
                    onboardingResponse?.data ||
                    onboardingResponse;

                console.log('✅ resolvedOnboardingData:', resolvedOnboardingData);
                console.log('✅ resolvedOnboardingData.name:', resolvedOnboardingData?.name);

                // Merge: userDetails first, then onboarding overwrites (onboarding takes priority)
                const mergedData = {
                    ...userDetails,
                    ...(resolvedOnboardingData || {}),
                };

                console.log('🔀 FINAL mergedData.name:', mergedData?.name);
                console.log('🔀 userDetails.name was:', userDetails?.name);
                console.log('🔀 resolvedOnboardingData.name was:', resolvedOnboardingData?.name);

                // Display name logic - exact same as profile page line 952
                const displayName = mergedData?.name || mergedData?.email?.split('@')[0] || 'User';
                
                console.log('📝 FINAL displayName that will be used:', displayName);
                console.log('========== END DEBUG ==========');
                
                if (!cancelled) {
                    setProfileDisplayName(displayName);
                }
            } catch (err) {
                console.error('Failed to fetch profile name for feed post', err);
                if (!cancelled) {
                    setProfileDisplayName('User');
                }
            }
        };

        fetchProfileName();

        return () => {
            cancelled = true;
        };
    }, [isOpen]);
    const handleClose = useCallback(() => {
        resetForm();
        onClose();
    }, [resetForm, onClose]);

    const handleFileSelect = useCallback((event) => {
        console.log('📁 File input changed, event:', event);
        const file = event.target.files?.[0];
        console.log('📁 Selected file:', file);
        
        if (!file) {
            console.log('❌ No file selected');
            return;
        }

        setError(null);

        // Validate file type
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');
        console.log('📁 File type check:', { type: file.type, isImage, isVideo });

        if (!isImage && !isVideo) {
            console.log('❌ Invalid file type');
            setError('Please select an image or video file');
            return;
        }

        // Validate file size (50MB max for videos, 10MB for images)
        const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
        console.log('📁 File size check:', { size: file.size, maxSize, passes: file.size <= maxSize });
        
        if (file.size > maxSize) {
            console.log('❌ File too large');
            setError(`File size must be less than ${isVideo ? '50MB' : '10MB'}`);
            return;
        }

        console.log('✅ File validation passed, setting media file');
        setMediaFile(file);
        setMediaType(isImage ? 'image' : 'video');

        // Create preview URL using object URL
        if (mediaPreviewUrl) {
            URL.revokeObjectURL(mediaPreviewUrl);
        }
        const previewUrl = URL.createObjectURL(file);
        setMediaPreview(previewUrl);
        setMediaPreviewUrl(previewUrl);
    }, [mediaPreviewUrl]);

    const removeMedia = useCallback(() => {
        setMediaFile(null);
        setMediaType(null);
        if (mediaPreviewUrl) {
            URL.revokeObjectURL(mediaPreviewUrl);
            setMediaPreviewUrl(null);
        }
        setMediaPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [mediaPreviewUrl]);

    const uploadToGCS = async (signedUrl, file) => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const progress = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(progress);
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                } else {
                    reject(new Error(`Upload failed with status ${xhr.status}`));
                }
            });

            xhr.addEventListener('error', () => {
                reject(new Error('Upload failed'));
            });

            xhr.open('PUT', signedUrl);
            xhr.setRequestHeader('Content-Type', file.type);
            xhr.send(file);
        });
    };

    const handleSubmit = async () => {
        if (!text.trim() && !mediaFile) {
            setError('Please add some text or media');
            return;
        }

        setIsUploading(true);
        setError(null);
        setUploadProgress(0);

        try {
            let mediaUrl = null;

            // If there's a media file, get signed URL and upload
            if (mediaFile) {
                // Get file extension
                const extension = mediaFile.name.split('.').pop().toLowerCase();
                
                // Request signed URL from backend
                const uploadUrlPayload = {
                    file_extension: extension,
                    content_type: mediaFile.type
                };
                console.log('📤 Requesting signed URL with payload:', uploadUrlPayload);
                
                const signedUrlResponse = await apiCall.post('/feed/get-upload-url', uploadUrlPayload);
                console.log('📥 Signed URL response:', signedUrlResponse);

                const signedUrl = signedUrlResponse?.signed_url || signedUrlResponse?.data?.signed_url;
                const publicUrl = signedUrlResponse?.public_url || signedUrlResponse?.data?.public_url;

                if (!signedUrl || !publicUrl) {
                    throw new Error('Failed to get upload URL');
                }

                // Upload file to GCS using signed URL
                console.log('⬆️ Uploading file to GCS:', {
                    signedUrl,
                    publicUrl,
                    fileType: mediaFile.type,
                    fileSize: mediaFile.size
                });
                await uploadToGCS(signedUrl, mediaFile);
                console.log('✅ File uploaded successfully');
                
                mediaUrl = publicUrl;
            }

            // Create the feed post
            const postData = {
                text: text.trim(),
                ...(mediaUrl && { image_url: mediaUrl }),
                ...(profileDisplayName ? { user_name: profileDisplayName } : {}),
            };
            console.log('📤 Creating feed post with payload:', postData);

            const createResponse = await apiCall.post('/feed/create', postData);
            console.log('📥 Feed create response:', createResponse);

            if (createResponse?.status || createResponse?._id) {
                // Post created successfully
                if (onPostCreated) {
                    onPostCreated(createResponse);
                }
                handleClose();
            } else {
                throw new Error('Failed to create post');
            }
        } catch (err) {
            console.error('Error creating post:', err);
            setError(err.message || 'Failed to create post. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    if (!isOpen) return null;

    const canSubmit = (text.trim() || mediaFile) && !isUploading;

    return (
        <div className="create-post-overlay" onClick={handleClose}>
            <div className="create-post-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="create-post-header">
                    <button 
                        className="create-post-close-btn" 
                        onClick={handleClose}
                        disabled={isUploading}
                    >
                        <X size={24} />
                    </button>
                    <h2 className="create-post-title">Create Post</h2>
                    <button 
                        className={`create-post-submit-btn ${canSubmit ? 'active' : ''}`}
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                    >
                        {isUploading ? (
                            <Loader size={20} className="spinner" />
                        ) : (
                            <Send size={20} />
                        )}
                    </button>
                </div>

                {/* Content */}
                <div className="create-post-content">
                    {/* Text Input */}
                    <textarea
                        className="create-post-textarea"
                        placeholder="What's on your mind?"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        disabled={isUploading}
                        maxLength={2000}
                    />

                    {/* Character count */}
                    <div className="create-post-char-count">
                        {text.length}/2000
                    </div>

                    {/* Media Preview */}
                    {mediaPreview && (
                        <div className="create-post-media-preview">
                            {mediaType === 'video' ? (
                                <video 
                                    src={mediaPreview} 
                                    controls 
                                    className="preview-media"
                                />
                            ) : (
                                <img 
                                    src={mediaPreview} 
                                    alt="Preview" 
                                    className="preview-media"
                                />
                            )}
                            {!isUploading && (
                                <button 
                                    className="remove-media-btn" 
                                    onClick={removeMedia}
                                >
                                    <X size={20} />
                                </button>
                            )}
                        </div>
                    )}

                    {/* Upload Progress */}
                    {isUploading && uploadProgress > 0 && (
                        <div className="upload-progress-container">
                            <div className="upload-progress-bar">
                                <div 
                                    className="upload-progress-fill"
                                    style={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                            <span className="upload-progress-text">
                                Uploading... {uploadProgress}%
                            </span>
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="create-post-error">
                            {error}
                        </div>
                    )}
                </div>

                {/* Footer - Media Actions */}
                <div className="create-post-footer">
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept="image/*,video/*"
                        style={{ display: 'none' }}
                        disabled={isUploading}
                    />
                    
                    <button 
                        className="media-action-btn"
                        onClick={() => {
                            console.log('📷 Photo button clicked, fileInputRef:', fileInputRef.current);
                            fileInputRef.current?.click();
                        }}
                        disabled={isUploading || !!mediaFile}
                    >
                        <Image size={24} />
                        <span>Photo</span>
                    </button>
                    
                    <button 
                        className="media-action-btn"
                        onClick={() => {
                            console.log('🎥 Video button clicked, fileInputRef:', fileInputRef.current);
                            fileInputRef.current?.click();
                        }}
                        disabled={isUploading || !!mediaFile}
                    >
                        <Video size={24} />
                        <span>Video</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreatePost;
